import axios, { CancelTokenSource } from 'axios';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import PxGrid from 'components/layout/PxGrid';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { MedicamentConsumerRenderProps, withMedicamentContext } from 'components/medicaments/MedicamentsContext';
import SearchResultComponent from 'components/SearchResultComponent';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Segment } from 'semantic-ui-react';
import {
  getAllSelectedMedicamentsByTariffCode,
  getAllTariffCodes,
  searchMedicaments,
  updateMedicamentSelection
} from 'services/medicamentServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { MedicamentDto, MedicamentEnumDto, MedicamentSearchRequest } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const StyledTableRow = styled.div`

  &.ReactVirtualized__Table__row.evenRow:hover {
    background-color: #fff !important;
  }

  &.ReactVirtualized__Table__row.oddRow:hover {
    background-color: #fafafa !important;
  }

  &.ReactVirtualized__Table__row.oddRow:hover,
  &.ReactVirtualized__Table__row.evenRow:hover {
    color: rgb(198, 198, 203) !important;
  }

`;

const StyledGrid = styled(Grid)`
  flex: 1 0 200px;
`;

const StyledMedicament = styled(Segment)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ButtonsDivider = styled.div`
  margin-top: 1rem;
`;

const TitlesDivider = styled.div`
  margin-bottom: 1rem;
`;

interface Props extends MessageSourceProps, MedicamentConsumerRenderProps, NotificationSystemProps {
}

interface State {
  foundMedicaments: Array<MedicamentDto>,
  selectedMedicaments: Array<MedicamentDto>,
  selectedMedicamentsSet: Set<number>,
  medicamentsLoaded: boolean,
  selectedMedicamentsLoaded: boolean,
  cancelTokenSource: CancelTokenSource,
  tariffCodes: Array<MedicamentEnumDto>,
  selectedTariffCode?: string | undefined,
  searchBy: string,
  scrollToIndex?: number,
  selectionIndex?: number,
  selectionRow?: MedicamentDto,
}

class MedicamentsSelectionView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundMedicaments: [],
      selectedMedicaments: [],
      selectedMedicamentsSet: new Set(),
      medicamentsLoaded: false,
      selectedMedicamentsLoaded: false,
      cancelTokenSource,
      tariffCodes: [],
      selectedTariffCode: undefined,
      searchBy: "code",
      scrollToIndex: 0,
      selectionIndex: 0
    };

    getAllTariffCodes(cancelTokenSource)
        .then(response => {
          this.setState({
            tariffCodes: response
          })
        })
        .catch(noop);
  }

  sortByDescription = (med1: MedicamentDto, med2: MedicamentDto) => {

    if (med1.description < med2.description) {
      return -1;
    }

    if (med1.description > med2.description) {
      return 1;
    }

    return 0;
  };

  searchByCriteria = (criteria: string, values: Partial<MedicamentSearchRequest>) => {

    this.setState({
      searchBy: criteria
    });

    this.handleSearchCriteriaChange(values);
  };

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {selectedTariffCode, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        selectedMedicamentsLoaded: true
      })
    };

    if (selectedTariffCode && prevState.selectedTariffCode !== selectedTariffCode) {
      getAllSelectedMedicamentsByTariffCode(selectedTariffCode.toString(), cancelTokenSource)
          .then((response: any) => {

            const selectedMedicamentsSet: Set<number> = new Set(response
                .filter(this.findSelectedMedicaments)
                .map((med: MedicamentDto) => med.id));

            this.setState({
              selectedMedicaments: response,
              selectedMedicamentsSet: selectedMedicamentsSet
            })
          })
          .catch(noop)
          .finally(onFinally);
    }
  }

  componentWillUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): void {

    const {selectedTariffCode} = this.state;

    if (nextState.selectedTariffCode !== selectedTariffCode) {
      this.setState({
        selectedMedicamentsLoaded: false
      })
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  handleSubmit = (event: any) => {
    event.preventDefault();

    const {
      selectionNotificationSystem,
      getMessage
    } = this.props;

    const {
      cancelTokenSource,
      selectedMedicamentsSet,
      selectedTariffCode
    } = this.state;

    //@ts-ignore
    const selectedMedicaments: Array<number> = [...selectedMedicamentsSet];

    if (selectedTariffCode) {
      updateMedicamentSelection(selectedMedicaments, selectedTariffCode, cancelTokenSource)
          .then(() => {

            //@ts-ignore
            const notification = selectionNotificationSystem.current;
            if (notification) {
              notification.addNotification({
                title: getMessage("medicament.update.success"),
                message: getMessage("medicament.successful.selection"),
                level: 'success'
              });
            }
          }).catch(noop)
    }
  };

  findSelectedMedicaments = (medicament: MedicamentDto) => {
    return medicament.selection;
  };

  handleSearchCriteriaChange = debounce((values: Partial<MedicamentSearchRequest>): void => {
    if (!this.mounted) {
      return;
    }
    const {cancelTokenSource, searchBy} = this.state;
    this.setState({
      medicamentsLoaded: false
    });

    let searchRequest: Partial<MedicamentSearchRequest> = {
      query: values.query || "",
      tariffCode: values.tariffCode,
      selectedOnly: false
    }

    const onFinally = () => {
      this.setState({
        medicamentsLoaded: true
      })
    };

    searchMedicaments(searchRequest, cancelTokenSource)
        .then(response => {

          this.setState({
            foundMedicaments: response,
            selectedTariffCode: values.tariffCode,
            scrollToIndex: undefined
          });
        })
        .catch(noop)
        .finally(onFinally);

  }, 300);

  render(): React.ReactNode {

    const {getMessage} = this.props;

    const {
      medicamentsLoaded,
      selectedMedicamentsLoaded,
      selectedMedicaments,
      foundMedicaments
    } = this.state;

    const numSelected = selectedMedicaments.length;
    const numResults = foundMedicaments.length;

    return (
        <StyledMedicament>
          <div>
            {this.renderSearchMedicamentSegment()}
          </div>
          <TitlesDivider>
            <StyledGrid>
              <Grid.Column width={16}>
              </Grid.Column>
            </StyledGrid>
            <StyledGrid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Header size="small" width={8}>
                    <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
                  </Header>
                </Grid.Column>
                <Grid.Column width={8} verticalAlign="middle">
                  <Header size="small" width={8}>
                    <SearchResultComponent numSelected={numSelected} textMessage={"msgMedicineSelection"}/>
                  </Header>
                </Grid.Column>
              </Grid.Row>
            </StyledGrid>
          </TitlesDivider>
          <StyledGrid>
            <Grid.Row>
              <Grid.Column width={8}>
                {!medicamentsLoaded ?
                    <LoaderComponent message={getMessage("medicament.loading")}/> :
                    this.renderMedicamentsSegment()}
              </Grid.Column>
              <Grid.Column width={8}>
                {!selectedMedicamentsLoaded
                    ? <LoaderComponent message={getMessage("medicament.loading")}/>
                    : this.renderSelectedMedicaments()}
              </Grid.Column>
            </Grid.Row>
          </StyledGrid>
          <ButtonsDivider>
            <Button compact type="submit" onClick={this.handleSubmit} primary>
              {getMessage("button.save")}
            </Button>
          </ButtonsDivider>
        </StyledMedicament>
    );
  }

  renderSearchMedicamentSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              tariffCode: 402
            }}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;

    const {
      tariffCodes,
      searchBy
    } = this.state;

    const tariffCodeOptions = tariffCodes ? tariffCodes.map(tariffCode => {

      return {
        key: parseInt(tariffCode.code),
        text: `${tariffCode.code}, ${getMessage(`tc.${tariffCode.name}`)}`,
        value: parseInt(tariffCode.code)
      }
    }) : [{key: 400, value: 400, text: `${getMessage(`tc.medicine`)}`}];

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={2} verticalAlign="middle">
              {getMessage("tariff")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="tariffCode"
                  component={Select}
                  fluid
                  options={tariffCodeOptions}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={2} verticalAlign="middle">
              {`${getMessage("eanCode")} / ${getMessage("pharmaCode")} / ${getMessage("description")}`}:
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  name="query"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <FormSpy subscription={{values: true}}
                   onChange={formState => this.handleSearchCriteriaChange(formState.values)}/>
        </PxGrid>
    )
  };

  selectMedicament = (index: number) => {

    const {
      foundMedicaments,
      selectedMedicaments,
      selectedMedicamentsSet
    } = this.state;

    const selected = selectedMedicaments.find(
        medicament => medicament.id === foundMedicaments[index].id);

    if (!selected) {
      this.setState((prevState: Readonly<State>) => {

            selectedMedicamentsSet.add(foundMedicaments[index].id);
            foundMedicaments[index].selection = true;

            const sortSelected = [...prevState.selectedMedicaments, foundMedicaments[index]];
            sortSelected.sort(this.sortByDescription);

            const selectionIndex = sortSelected.findIndex(
                med => med.id === foundMedicaments[index].id);

            return {
              foundMedicaments: foundMedicaments,
              selectedMedicaments: sortSelected,
              selectedMedicamentsSet: selectedMedicamentsSet,
              scrollToIndex: undefined,
              selectionRow: foundMedicaments[index],
              selectionIndex: selectionIndex
            }
          }
      );
    }
  };

  foundMedicamentsRowGetter = ({index}: any) => {
    const {foundMedicaments} = this.state;

    return foundMedicaments[index];
  };

  foundMedicamentsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundMedicaments, scrollToIndex} = this.state;

    if (foundMedicaments[index].selection) {
      style.color = '#c6c6cb';
      return (
          <StyledTableRow
              {...a11yProps}
              className={className}
              key={key}
              role="row"
              style={style}
              onDoubleClick={() => this.selectMedicament(index)}
          >
            {columns}
          </StyledTableRow>
      );

    }

    if (scrollToIndex === index) {
      style.background = "#b7b7b7";
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.selectMedicament(index)}
        >
          {columns}
        </div>
    );
  };

  renderMedicamentsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundMedicaments, scrollToIndex} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundMedicaments.length}
            rowGetter={this.foundMedicamentsRowGetter}
            rowRenderer={this.foundMedicamentsRowRenderer}
            scrollToIndex={scrollToIndex}
            rowHeight={42}
            columns={[
              {
                width: 250,
                label: "Code",
                dataKey: "code"
              },
              {
                width: 800,
                label: (getMessage("description")),
                dataKey: "description"
              }
            ]}
        />
    );
  };

  removeSelectedMedicament = (index: any) => {

    const {
      selectedMedicaments,
      selectedMedicamentsSet,
      foundMedicaments
    } = this.state;

    let modifiedSelectedMedicament = {...selectedMedicaments[index]};
    modifiedSelectedMedicament.selection = false;

    const medicamentId = modifiedSelectedMedicament.id;

    const medicamentExists = foundMedicaments.some(
        medicament => medicament.id === medicamentId);

    const scrollToIndex = medicamentExists
        ? foundMedicaments.findIndex(medicament => medicament.id === medicamentId)
        : foundMedicaments.length;

    selectedMedicamentsSet.delete(medicamentId);

    if (!medicamentExists) {
      foundMedicaments.push(modifiedSelectedMedicament);
    }

    foundMedicaments[scrollToIndex].selection = false;
    selectedMedicaments.splice(index, 1);

    this.setState(() => ({
      foundMedicaments: foundMedicaments,
      selectedMedicaments: selectedMedicaments,
      selectedMedicamentsSet: selectedMedicamentsSet,
      scrollToIndex: scrollToIndex,
      selectionRow: undefined
    }));

  };

  selectedMedicamentsRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};
    const {selectedMedicaments, selectionRow} = this.state;

    if (selectionRow !== undefined && selectionRow === selectedMedicaments[index]) {
      style.background = "#b7b7b7";
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.removeSelectedMedicament(index)}
        >
          {columns}
        </div>
    );
  };

  renderSelectedMedicaments = (): JSX.Element => {

    const {getMessage} = this.props;
    const {selectedMedicaments, selectionIndex} = this.state;

    return (
        <VirtualizedTable
            rowCount={selectedMedicaments.length}
            rowGetter={({index}: any) => selectedMedicaments[index]}
            rowRenderer={this.selectedMedicamentsRowRenderer}
            scrollToIndex={selectionIndex}
            rowHeight={42}
            columns={[
              {
                width: 250,
                label: "Code",
                dataKey: "code"
              },
              {
                width: 800,
                label: (getMessage("description")),
                dataKey: "description"
              }
            ]}
        />
    );
  };

}

const MedicamentsSelectionViewWrapper =
    withMessages(
        withMedicamentContext(
            withNotificationSystemContext(
                MedicamentsSelectionView)));


export default MedicamentsSelectionViewWrapper;
